import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { AppSpec, User } from '../types';
import { listApps, deleteApp, downloadAppFiles, saveApp } from '../services/api';
import { buildZip, downloadBlob } from '../utils/zip';
import { iconEmoji } from '../utils/icons';
import '../styles/AppListPage.css';

interface Props {
  user: User;
  onCreateNew: () => void;
  onEditApp: (appId: string) => void;
  onStandaloneDeploy: () => void;
}

type SortKey = 'newest' | 'oldest' | 'name' | 'status';

const AppListPage: React.FC<Props> = ({ user, onCreateNew, onEditApp, onStandaloneDeploy }) => {
  const [apps, setApps] = useState<AppSpec[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'draft' | 'deployed' | 'failed'>('all');
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('newest');
  const [busy, setBusy] = useState<string | null>(null); // appId of in-flight action

  const loadApps = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await listApps();
      setApps(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load apps:', err);
      setLoadError(err instanceof Error ? err.message : 'Failed to load apps');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadApps();
  }, [loadApps]);

  const handleDelete = async (appId: string) => {
    if (!window.confirm('Are you sure you want to delete this app spec? (Deployed infrastructure is not removed.)')) {
      return;
    }
    setBusy(appId);
    try {
      await deleteApp(appId);
      setApps(prev => prev.filter(a => a.id !== appId));
    } catch (err) {
      console.error('Failed to delete app:', err);
      alert(`Failed to delete: ${err instanceof Error ? err.message : 'unknown error'}`);
    } finally {
      setBusy(null);
    }
  };

  const handleDownload = async (app: AppSpec) => {
    setBusy(app.id);
    try {
      const data = await downloadAppFiles(app.id);
      if (!data.files || data.files.length === 0) {
        alert('No generated files found for this app yet. Generate it first.');
        return;
      }
      const name = app.name || app.subdomain || 'app';
      const zip = buildZip(
        data.files.map(f => ({ path: `${name}/${f.path.replace(/^\/+/, '')}`, content: f.content }))
      );
      downloadBlob(zip, `${name}-source.zip`);
    } catch (err) {
      console.error('Failed to download source code:', err);
      alert(`Download failed: ${err instanceof Error ? err.message : 'unknown error'}`);
    } finally {
      setBusy(null);
    }
  };

  const handleExportSpec = (app: AppSpec) => {
    const blob = new Blob([JSON.stringify(app, null, 2)], { type: 'application/json' });
    downloadBlob(blob, `${app.subdomain || app.id}-spec.json`);
  };

  const handleDuplicate = async (app: AppSpec) => {
    const subdomain = window.prompt(
      'New subdomain for the duplicated spec:',
      `${app.subdomain}-copy`
    );
    if (!subdomain) return;
    const clean = subdomain.toLowerCase().replace(/[^a-z0-9-]/g, '');
    if (!clean) { alert('Invalid subdomain'); return; }
    if (apps.some(a => a.subdomain === clean)) { alert(`Subdomain "${clean}" already exists`); return; }

    setBusy(app.id);
    try {
      const copy: AppSpec = {
        ...app,
        id: '',
        subdomain: clean,
        name: clean,
        displayName: `${app.displayName} (copy)`,
        status: 'draft',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        createdBy: user.username,
      };
      const saved = await saveApp(copy);
      setApps(prev => [saved, ...prev]);
    } catch (err) {
      alert(`Failed to duplicate: ${err instanceof Error ? err.message : 'unknown error'}`);
    } finally {
      setBusy(null);
    }
  };

  const counts = useMemo(() => ({
    all: apps.length,
    draft: apps.filter(a => a.status === 'draft').length,
    deployed: apps.filter(a => a.status === 'deployed').length,
    failed: apps.filter(a => a.status === 'failed').length,
  }), [apps]);

  const visibleApps = useMemo(() => {
    let list = apps;
    if (filter !== 'all') list = list.filter(a => a.status === filter);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(a =>
        (a.displayName || '').toLowerCase().includes(q) ||
        (a.subdomain || '').toLowerCase().includes(q) ||
        (a.description || '').toLowerCase().includes(q)
      );
    }
    const sorted = [...list];
    switch (sortKey) {
      case 'newest':
        sorted.sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
        break;
      case 'oldest':
        sorted.sort((a, b) => (a.createdAt || '').localeCompare(b.createdAt || ''));
        break;
      case 'name':
        sorted.sort((a, b) => (a.displayName || '').localeCompare(b.displayName || ''));
        break;
      case 'status':
        sorted.sort((a, b) => (a.status || '').localeCompare(b.status || ''));
        break;
    }
    return sorted;
  }, [apps, filter, search, sortKey]);

  const getStatusLabel = (status: AppSpec['status']): string => {
    switch (status) {
      case 'draft': return 'Draft';
      case 'generating': return 'Generating…';
      case 'deployed': return 'Deployed';
      case 'failed': return 'Failed';
      default: return status;
    }
  };

  if (loading) {
    return (
      <div className="app-list-page">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading apps…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="app-list-page">
      <div className="page-header">
        <div className="header-content">
          <h1>My Apps</h1>
          <p className="subtitle">Create and manage your spec-driven applications</p>
        </div>
        <div className="header-actions">
          {user.isAdmin && (
            <button
              className="btn-secondary standalone-entry"
              onClick={onStandaloneDeploy}
              title="Deploy and manage self-hosted, encrypted apps"
            >
              🔒 Standalone Deploy
            </button>
          )}
          <button className="btn-primary create-btn" onClick={onCreateNew}>
            + Create New App
          </button>
        </div>
      </div>

      {loadError && (
        <div className="error-banner">
          Failed to load apps: {loadError}
          <button className="btn-secondary" onClick={loadApps} style={{ marginLeft: '0.75rem' }}>Retry</button>
        </div>
      )}

      <div className="toolbar">
        <div className="filter-bar">
          {(['all', 'draft', 'deployed', 'failed'] as const).map(f => (
            <button key={f} className={filter === f ? 'active' : ''} onClick={() => setFilter(f)}>
              {f === 'all' ? 'All' : getStatusLabel(f as AppSpec['status'])} ({counts[f]})
            </button>
          ))}
        </div>
        <div className="toolbar-right">
          <input
            type="search"
            className="app-search"
            placeholder="Search apps…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <select className="app-sort" value={sortKey} onChange={e => setSortKey(e.target.value as SortKey)}>
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
            <option value="name">Name A–Z</option>
            <option value="status">Status</option>
          </select>
          <button className="btn-secondary refresh-btn" onClick={loadApps} title="Refresh">↻</button>
        </div>
      </div>

      {visibleApps.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📦</div>
          <h2>{apps.length === 0 ? 'No apps yet' : 'No apps match your filters'}</h2>
          <p>{apps.length === 0 ? 'Create your first spec-driven application' : 'Try a different search or filter'}</p>
          {apps.length === 0 && (
            <button className="btn-primary" onClick={onCreateNew}>Create New App</button>
          )}
        </div>
      ) : (
        <div className="apps-grid">
          {visibleApps.map(app => (
            <div key={app.id} className={`app-card status-${app.status}`}>
              <div className="app-card-header">
                <div className="app-icon">{iconEmoji(app.icon)}</div>
                <span className={`app-status badge-${app.status}`}>{getStatusLabel(app.status)}</span>
              </div>

              <div className="app-card-content">
                <h3>{app.displayName}</h3>
                <p className="app-description">{app.description}</p>
                <a
                  className="app-url"
                  href={`https://${app.subdomain}.sae-g.com`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {app.subdomain}.sae-g.com
                </a>

                <div className="app-meta">
                  <span>{app.skills?.length || 0} skills</span>
                  <span>{app.requirements?.length || 0} requirements</span>
                  {app.steering?.generationMethod && (
                    <span>{app.steering.generationMethod === 'template' ? 'Template' : 'AI'}</span>
                  )}
                </div>
              </div>

              <div className="app-card-actions">
                {app.status === 'deployed' && (
                  <a
                    href={`https://${app.subdomain}.sae-g.com`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-secondary"
                  >
                    Open
                  </a>
                )}
                <button className="btn-secondary" onClick={() => onEditApp(app.id)}>Edit</button>
                <button
                  className="btn-secondary"
                  onClick={() => handleDownload(app)}
                  disabled={busy === app.id}
                  title="Download generated source as .zip"
                >
                  {busy === app.id ? '…' : 'Source.zip'}
                </button>
                <button
                  className="btn-secondary"
                  onClick={() => handleExportSpec(app)}
                  title="Export spec as JSON"
                >
                  Spec
                </button>
                <button
                  className="btn-secondary"
                  onClick={() => handleDuplicate(app)}
                  disabled={busy === app.id}
                  title="Duplicate this spec under a new subdomain"
                >
                  Duplicate
                </button>
                {user.isAdmin && (
                  <button className="btn-danger" onClick={() => handleDelete(app.id)} disabled={busy === app.id}>
                    Delete
                  </button>
                )}
              </div>

              <div className="app-card-footer">
                <small>Created {new Date(app.createdAt).toLocaleDateString()}</small>
                <small>by {app.createdBy}</small>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AppListPage;
